/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import React, { useCallback, useMemo } from 'react';
import { InputColumn, TableInputProps } from './InputInterface';
import { TableInputForm } from './TableInputForm';
import { isColumnVisible, recalculateRowColumns, recalculateVisibilityRules, VisibilityResult } from './calculation';
import { RowStatus } from '../../../question/questionInterfaces';
import { generateDecimalErrorMessage } from '../../../utils/input';

export default function SingleRowTableInput(props: TableInputProps) {
  const { table, tableConfiguration, calculationColumns, visibilityRuleColumns, universalTracker } = props;
  
  const firstRow = useMemo(() => {
    const { rows } = table;
    const initialRow = rows[0] ?? { rowStatus: RowStatus.added, id: 0, data: [] };
    initialRow.data =
      initialRow.data.length > 0 ? initialRow.data : tableConfiguration.columns.map((c) => ({ code: c.code }));
    return initialRow;
  }, [table, tableConfiguration.columns]);

  const [formRow, setFormRowData] = React.useState<InputColumn[]>(firstRow.data);
  const [visibilityResults, setVisibilityResults] = React.useState<VisibilityResult>();

  const setFormRow = useCallback((data: InputColumn[]) => {
    const {visibleColumns, visibleResults} = recalculateVisibilityRules(data, visibilityRuleColumns, {} )
    setVisibilityResults(visibleResults)
    setFormRowData(
      visibleColumns
    );
  }, [visibilityRuleColumns]);

  // Initialize columns validation
  React.useEffect(() => {
    setFormRow(firstRow.data);
  }, [tableConfiguration, setFormRow, firstRow.data]);

  return (
    <div className='table-input single-row'>
      <TableInputForm
        {...props}
        row={formRow}
        multiRow={false}
        isColumnVisible={(code: string) => isColumnVisible(code, visibilityResults)}
        updateColumn={(column: InputColumn) => {
          const nextRowData = recalculateRowColumns(formRow, column, calculationColumns);
          setFormRow(nextRowData)
          const inputMessage = generateDecimalErrorMessage({
            utr: universalTracker,
            input: Object.fromEntries(nextRowData.map((k) => [[k.code], k.value])),
          });
          props.updateTable({ rows: [{ ...firstRow, data: nextRowData }] }, inputMessage)
        }}
      />
    </div>
  );
}
